import { useContext } from "react";
import AuthContext from "../providers/auth/AuthContext";
import { AuthContextType } from "../types/auth";

/**
 * Consolidated authentication hook that provides all auth functionality
 *
 * This is the single source of truth for authentication in the application.
 * It exposes the auth context with all state and actions needed for:
 * - Login/logout
 * - MFA verification
 * - Password reset
 * - User state management
 * - Error handling
 *
 * Usage:
 * ```tsx
 * const auth = useAuth();
 *
 * // Access state
 * if (auth.isAuthenticated) { ... }
 * if (auth.requiresMfa) { ... }
 *
 * // Perform actions
 * await auth.login({ email, password });
 * await auth.verifyMfa({ email, code });
 * await auth.logout();
 * ```
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
